import axios, { AxiosError } from "axios";
import type { AppError, AppErrorType, FieldError, TokenPair } from "@/types";
import {
  TOKEN_KEYS,
  clearSharedTokens,
  hydrateSharedTokens,
  persistSharedTokens,
} from "@/utils/authSession";

/**
 * Centralised Axios instance for the IELTS backend.
 *
 * Responsibilities:
 *  - Base URL from environment (VUE_APP_API_URL)
 *  - Timeout handling
 *  - Attaches the JWT access token on every request
 *  - Transparently refreshes an expired access token once, then retries
 *  - Normalises errors into a consistent shape for the error handler
 */

const BASE_URL =
  process.env.VUE_APP_API_URL || "http://localhost:8000/api/v1";
const TIMEOUT = Number(process.env.VUE_APP_API_TIMEOUT) || 15000;

export { TOKEN_KEYS };

interface PendingRequest {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}

interface FastApiValidationDetail {
  loc?: string | (string | number)[];
  msg: string;
}

interface BackendErrorBody {
  detail?: string | FastApiValidationDetail[];
  error?: { message?: string };
}

// ---- Token storage helpers ----
export const tokenStorage = {
  getAccess: (): string | null => {
    hydrateSharedTokens();
    return localStorage.getItem(TOKEN_KEYS.access);
  },
  getRefresh: (): string | null => {
    hydrateSharedTokens();
    return localStorage.getItem(TOKEN_KEYS.refresh);
  },
  set: ({ access_token, refresh_token }: TokenPair): void => {
    if (access_token) localStorage.setItem(TOKEN_KEYS.access, access_token);
    if (refresh_token) localStorage.setItem(TOKEN_KEYS.refresh, refresh_token);
    persistSharedTokens({ access_token, refresh_token });
  },
  clear: (): void => {
    localStorage.removeItem(TOKEN_KEYS.access);
    localStorage.removeItem(TOKEN_KEYS.refresh);
    clearSharedTokens();
  },
};

// Simple event bus so the auth layer/UI can react to forced logout.
export const authEvents = new EventTarget();
export const emitUnauthorized = (): boolean =>
  authEvents.dispatchEvent(new CustomEvent("unauthorized"));

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: { Accept: "application/json" },
});

// A bare client used only for token refresh to avoid interceptor recursion.
const refreshClient = axios.create({ baseURL: BASE_URL, timeout: TIMEOUT });

// ---- Request interceptor: attach bearer token ----
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenStorage.getAccess();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Refresh handling (single-flight) ----
let isRefreshing = false;
let pendingQueue: PendingRequest[] = [];

const resolveQueue = (error: unknown, token: string | null = null): void => {
  pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  pendingQueue = [];
};

async function refreshAccessToken(): Promise<string | undefined> {
  const refresh_token = tokenStorage.getRefresh();
  if (!refresh_token) throw new Error("No refresh token available");
  const { data } = await refreshClient.post<TokenPair>("/auth/refresh", {
    refresh_token,
  });
  tokenStorage.set(data);
  return data.access_token;
}

// ---- Response interceptor: refresh-on-401 then normalise errors ----
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const { config, response } = error;
    const original = config || {};

    // Attempt a one-time refresh when the access token is rejected.
    if (
      response &&
      response.status === 401 &&
      !original._retry &&
      tokenStorage.getRefresh() &&
      !original.url?.includes("/auth/")
    ) {
      original._retry = true;

      if (isRefreshing) {
        // Queue until the in-flight refresh resolves.
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${token}`;
          return apiClient(original);
        });
      }

      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        resolveQueue(null, newToken ?? null);
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      } catch (refreshError) {
        resolveQueue(refreshError, null);
        tokenStorage.clear();
        emitUnauthorized();
        return Promise.reject(normalizeError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    if (response && response.status === 401 && !isCredentialAuthRequest(original.url)) {
      tokenStorage.clear();
      emitUnauthorized();
    }

    return Promise.reject(normalizeError(error));
  }
);

function isCredentialAuthRequest(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/register") ||
    url.includes("/auth/otp/") ||
    url.includes("/auth/forgot-password") ||
    url.includes("/auth/reset-password")
  );
}

/**
 * Convert any Axios error into a predictable object:
 * { type, status, message, fieldErrors, original }
 */
export function normalizeError(error: unknown): AppError {
  const axiosError = error as AxiosError<BackendErrorBody> & {
    code?: string;
  };

  // Network / no response
  if (axiosError.code === "ECONNABORTED") {
    return {
      type: "timeout",
      status: 0,
      message: "زمان درخواست به پایان رسید. لطفاً دوباره تلاش کنید.",
      fieldErrors: null,
      original: error,
    };
  }
  if (!axiosError.response) {
    return {
      type: "network",
      status: 0,
      message: "خطای شبکه. اتصال و سرور را بررسی کنید.",
      fieldErrors: null,
      original: error,
    };
  }

  const { status, data } = axiosError.response;
  let message = "مشکلی پیش آمد.";
  let fieldErrors: FieldError[] | null = null;

  if (data) {
    if (typeof data.detail === "string") {
      message = data.detail;
    } else if (Array.isArray(data.detail)) {
      // FastAPI/Pydantic validation errors
      fieldErrors = data.detail.map((d) => ({
        field: String(
          Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : d.loc ?? ""
        ),
        message: d.msg,
      }));
      message = "لطفاً فیلدهای مشخص‌شده را اصلاح کنید.";
    } else if (data.error && data.error.message) {
      // Our backend's AppError shape
      message = data.error.message;
    }
  }

  let type: AppErrorType = "server";
  if (status === 401) type = "unauthorized";
  else if (status === 403) type = "forbidden";
  else if (status === 404) type = "not_found";
  else if (status === 422) type = "validation";

  return { type, status, message, fieldErrors, original: error };
}

export default apiClient;
